import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import TrackEditModal from "../components/TrackEditModal";
import ImportLinkModal from "../components/ImportLinkModal";
import PaywallModal from "../components/PaywallModal";
import TrackRow from "../components/TrackRow";
import ProjectGrid from "../components/ProjectGrid";
import ProjectView from "../components/ProjectView";
import LibraryActions from "../components/LibraryActions";
import { useAuth } from "../contexts/AuthContext";
import { useSubscription } from "../hooks/useSubscription";
import { RELEASED_STATUSES, UNRELEASED_STATUSES, SHELVED_STATUSES } from "../lib/statuses";
import api from "../lib/api";
import { toast } from "sonner";

export default function Dashboard() {
  const { user, loading, refreshBilling } = useAuth();
  const sub = useSubscription();
  const nav = useNavigate();
  const [tracks, setTracks] = useState([]);
  const [loadingTracks, setLoadingTracks] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [paywall, setPaywall] = useState({ open: false, reason: "default" });
  const [projects, setProjects] = useState([]);
  const [view, setView] = useState("projects");
  const [openProjectId, setOpenProjectId] = useState(null);

  useEffect(() => { if (!loading && !user) nav("/auth"); }, [loading, user, nav]);

  const load = useCallback(async () => {
    try {
      const [r, p] = await Promise.all([api.get("/tracks"), api.get("/projects")]);
      setTracks(r.data);
      setProjects(p.data);
    } finally {
      setLoadingTracks(false);
    }
  }, []);

  useEffect(() => { if (user) load(); }, [user, load]);

  // Listen for paywall events from child components
  useEffect(() => {
    const onPaywall = (e) => setPaywall({ open: true, reason: e.detail?.reason || "default" });
    window.addEventListener("lq:paywall", onPaywall);
    return () => window.removeEventListener("lq:paywall", onPaywall);
  }, []);

  const stats = useMemo(() => {
    const all = tracks.length;
    const released = tracks.filter((t) => RELEASED_STATUSES.includes(t.status)).length;
    const unreleased = tracks.filter((t) => UNRELEASED_STATUSES.includes(t.status)).length;
    const shelved = tracks.filter((t) => SHELVED_STATUSES.includes(t.status)).length;
    return { all, released, unreleased, shelved };
  }, [tracks]);

  const visible = useMemo(() => {
    let list = tracks;
    if (filter === "released") list = list.filter((t) => RELEASED_STATUSES.includes(t.status));
    else if (filter === "unreleased") list = list.filter((t) => UNRELEASED_STATUSES.includes(t.status));
    else if (filter === "shelved") list = list.filter((t) => SHELVED_STATUSES.includes(t.status));
    else if (filter === "incomplete") list = list.filter((t) => (t.completeness_score ?? 0) < 100);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((t) =>
        [t.title, t.isrc, t.iswc, t.notes, t.genre, t.label,
         t.producers, t.writers, t.primary_artist, t.features]
          .filter(Boolean).some((v) => String(v).toLowerCase().includes(q))
      );
    }
    return list;
  }, [tracks, filter, search]);

  const projectById = useMemo(() => Object.fromEntries(projects.map((p) => [p.id, p])), [projects]);
  const tracksByProject = useMemo(() => {
    const m = {};
    visible.forEach((t) => { if (t.project_id && projectById[t.project_id]) (m[t.project_id] = m[t.project_id] || []).push(t); });
    return m;
  }, [visible, projectById]);
  const narrowed = filter !== "all" || search.trim();
  const shownProjects = narrowed ? projects.filter((p) => tracksByProject[p.id]) : projects;
  const looseTracks = visible.filter((t) => !t.project_id || !projectById[t.project_id]);
  const openProject = openProjectId ? projectById[openProjectId] : null;

  const onNew = () => {
    if (!sub.canAddTrack) {
      setPaywall({ open: true, reason: "track_limit" });
      return;
    }
    setEditing({});
    setEditorOpen(true);
  };

  const onImport = () => {
    if (!sub.canImport) {
      setPaywall({ open: true, reason: "import_link" });
      return;
    }
    setImportOpen(true);
  };

  const onRowClick = (t) => {
    setEditing(t);
    setEditorOpen(true);
  };

  const onProjectCreated = (p) => setProjects((prev) => [p, ...prev]);

  const onSaved = (t) => {
    if (editing?.project_id !== t.project_id || !editing?.id) {
      api.get("/projects").then((r) => setProjects(r.data));
    }
    setTracks((prev) => {
      const idx = prev.findIndex((x) => x.id === t.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = t;
        return next;
      }
      return [t, ...prev];
    });
    refreshBilling();
  };

  const onDelete = async (t) => {
    try {
      await api.delete(`/tracks/${t.id}`);
      setTracks((prev) => prev.filter((x) => x.id !== t.id));
      if (t.project_id) api.get("/projects").then((r) => setProjects(r.data));
      toast.success("Track deleted");
      refreshBilling();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <>
      <Header />
      <div className="lq-container page-enter" data-testid="dashboard-page">
        <div style={{ marginBottom: 28 }}>
          <div className="eyebrow">— CAT.NO / TRACKER</div>
          <h1 className="serif" style={{ fontSize: 56, lineHeight: 0.95, margin: "10px 0 0", letterSpacing: "-0.02em" }}>
            Lacquer
          </h1>
        </div>

        {/* Stats */}
        <div className="stats-row" data-testid="stats-row">
          <Stat n={stats.all} label="Total" />
          <Stat n={stats.released} label="Released" accent />
          <Stat n={stats.unreleased} label="Unreleased" />
          <Stat n={stats.shelved} label="Shelved" />
        </div>

        {/* Subscription banner */}
        {sub.tier === "free" && stats.all >= 20 && (
          <div className="callout" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
            <div>
              <strong>{sub.trackLimit - stats.all} tracks left</strong> on the Free tier. Upgrade for unlimited.
            </div>
            <button className="btn btn-primary" onClick={() => nav("/pricing")} data-testid="banner-upgrade">Start Free Trial →</button>
          </div>
        )}
        {sub.status === "trialing" && sub.trialDaysRemaining !== null && (
          <div className="callout" style={{ background: "rgba(21, 128, 61, 0.06)", borderColor: "rgba(21, 128, 61, 0.20)" }}>
            <span style={{ color: "var(--green-light)" }}>● </span>
            <strong style={{ color: "var(--green-light)" }}>Pro trial</strong> · {sub.trialDaysRemaining} day{sub.trialDaysRemaining === 1 ? "" : "s"} remaining
            {!sub.hasCard && " · No card on file"}
          </div>
        )}

        {/* Controls */}
        <div style={{ display: "flex", gap: 10, marginBottom: 18, flexWrap: "wrap" }}>
          <input
            data-testid="search-input"
            className="lq-input"
            placeholder="Search title, ISRC, credits, notes…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: 1, minWidth: 200 }}
          />
          <button className="btn btn-light" onClick={onNew} data-testid="new-track-btn">+ New</button>
          <button className="btn btn-primary" onClick={onImport} data-testid="import-link-btn">⌫ Import Link</button>
        </div>

        <div style={{ display: "flex", gap: 8, marginBottom: 22, flexWrap: "wrap" }} data-testid="filter-pills">
          {["all", "released", "unreleased", "shelved", "incomplete"].map((f) => (
            <button
              key={f}
              className={`pill ${filter === f ? "active" : ""}`}
              onClick={() => setFilter(f)}
              data-testid={`filter-${f}`}
            >
              {f}
            </button>
          ))}
        </div>

        <LibraryActions onChanged={load} />

        <div className="view-toggle" data-testid="view-toggle">
          <button className={view === "projects" ? "active" : ""} onClick={() => setView("projects")} data-testid="view-by-project">By Project</button>
          <button className={view === "tracks" ? "active" : ""} onClick={() => { setView("tracks"); setOpenProjectId(null); }} data-testid="view-all-tracks">All Tracks</button>
        </div>

        <div data-testid="tracks-list">
          {loadingTracks && <div className="data-line" style={{ padding: 24 }}>Loading…</div>}
          {!loadingTracks && visible.length === 0 && !(view === "projects" && !narrowed && projects.length) && (
            <div className="callout" style={{ textAlign: "center", padding: 40 }}>
              {tracks.length === 0 ? (
                <>
                  Your library is empty. <strong>Click + New</strong> to add your first track or <strong>Import Link</strong> to paste a streaming URL.
                </>
              ) : (
                <>No tracks match your filter / search.</>
              )}
            </div>
          )}
          {!loadingTracks && view === "tracks" && visible.map((t, i) => (
            <TrackRow key={t.id} t={t} index={i} coverFallback={projectById[t.project_id]?.cover_art_url} onClick={onRowClick} />
          ))}
          {!loadingTracks && view === "projects" && openProject && (
            <ProjectView
              project={openProject}
              tracks={tracksByProject[openProject.id] || []}
              onBack={() => setOpenProjectId(null)}
              onRowClick={onRowClick}
              onDeleted={() => { setOpenProjectId(null); load(); }}
            />
          )}
          {!loadingTracks && view === "projects" && !openProject && (
            <>
              <ProjectGrid projects={shownProjects} tracksByProject={tracksByProject} onOpen={(p) => setOpenProjectId(p.id)} />
              {looseTracks.length > 0 && (
                <div data-testid="loose-tracks">
                  <div className="eyebrow" style={{ margin: "28px 0 4px" }}>— Not in a project · {looseTracks.length}</div>
                  {looseTracks.map((t, i) => <TrackRow key={t.id} t={t} index={i} onClick={onRowClick} />)}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <TrackEditModal
        open={editorOpen}
        track={editing}
        onClose={() => setEditorOpen(false)}
        onSaved={onSaved}
        onDelete={onDelete}
        projects={projects}
        onProjectCreated={onProjectCreated}
      />
      <ImportLinkModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={() => { load(); refreshBilling(); }}
      />
      <PaywallModal
        open={paywall.open}
        reason={paywall.reason}
        onClose={() => setPaywall({ open: false, reason: "default" })}
      />
    </>
  );
}

function Stat({ n, label, accent, testid }) {
  return (
    <div data-testid={testid}>
      <div className={`stat-num ${accent ? "accent" : ""}`}>
        {typeof n === "number" ? String(n).padStart(2, "0") : n}
      </div>
      <div className="stat-label">{label}</div>
    </div>
  );
}
